import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, KeyRound } from "lucide-react";
import { Logo } from "@/components/layout/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState(searchParams.get("token") ?? "");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? "https://api.credcapital.net";

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (next !== confirm) { setError("Senha e confirmação não conferem."); return; }
    if (next.length < 10) { setError("Senha precisa ter no mínimo 10 caracteres."); return; }
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE}/auth/password-reset/confirm`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: token.trim(), new_password: next }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        if (body.error === "token_expired") throw new Error("Link expirado. Solicite um novo.");
        if (body.error === "token_already_used") throw new Error("Link já usado. Solicite um novo.");
        if (body.error === "invalid_token") throw new Error("Link inválido.");
        throw new Error("Erro ao redefinir senha.");
      }
      setSuccess(true);
      setTimeout(() => navigate("/login", { replace: true }), 2500);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-muted/40 flex flex-col">
      <header className="bg-background border-b border-border">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center"><Logo /></Link>
          <Link to="/login" className="text-sm text-primary-medium inline-flex items-center gap-1.5">
            <ArrowLeft className="h-3.5 w-3.5" /> Voltar para login
          </Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="text-center mb-6">
            <div className="h-12 w-12 rounded-md bg-primary-dark text-accent inline-flex items-center justify-center mb-3">
              <KeyRound className="h-5 w-5" />
            </div>
            <h1 className="text-2xl font-display font-bold text-primary-dark">Definir nova senha</h1>
            <p className="text-sm text-muted-foreground mt-1">Use o token recebido por e-mail e cadastre uma senha forte.</p>
          </div>

          {success ? (
            <div className="bg-emerald-50 border border-emerald-200 rounded-md p-6 text-center">
              <div className="text-emerald-900 font-medium">Senha redefinida com sucesso!</div>
              <div className="text-sm text-emerald-800 mt-1">Redirecionando para login...</div>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4 bg-background border border-border rounded-md p-6">
              <div className="space-y-2">
                <Label htmlFor="token">Token de reset</Label>
                <Input id="token" required value={token} onChange={(e) => setToken(e.target.value)} placeholder="cole o token do email/link" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="next">Nova senha</Label>
                <Input id="next" type="password" minLength={10} required value={next} onChange={(e) => setNext(e.target.value)} />
                <p className="text-[11px] text-muted-foreground">Mínimo 10 caracteres.</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm">Confirmar nova senha</Label>
                <Input id="confirm" type="password" minLength={10} required value={confirm} onChange={(e) => setConfirm(e.target.value)} />
              </div>
              {error && <div className="text-sm text-destructive">{error}</div>}
              <Button type="submit" className="w-full" size="lg" disabled={loading}>
                {loading ? "Redefinindo..." : "Redefinir senha"}
              </Button>
            </form>
          )}
        </div>
      </main>
    </div>
  );
}
